import React, { useEffect, useRef } from 'react';
import { useFlyingCart } from '../contexts/FlyingCartContext';
import { useSound } from '../hooks/useSound';

const FlyingCartAnimation: React.FC = () => {
  const { flyingItems, removeFlyingItem } = useFlyingCart();
  const containerRef = useRef<HTMLDivElement>(null);
  const { play: playWhistleSound } = useSound('/sounds/whistle.mp3', { volume: 0.9 });

  useEffect(() => {
    // Cleanup old items after animation completes
    const timers: NodeJS.Timeout[] = [];
    
    flyingItems.forEach(item => {
      // Play whistle sound when spark starts flying
      playWhistleSound();
      
      // Trigger cart animation when spark reaches cart (at 90% of animation)
      const cartAnimTimer = setTimeout(() => {
        const event = new CustomEvent('flyingItemReachedCart');
        window.dispatchEvent(event);
      }, 720); // 0.72s - when animation is 90% complete (0.8s * 0.9)
      
      const timer = setTimeout(() => {
        removeFlyingItem(item.id);
      }, 1000); // Animation duration + buffer
      
      timers.push(timer);
      timers.push(cartAnimTimer);
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [flyingItems, removeFlyingItem]);

  const getCartIconPosition = () => {
    const cartIcon = document.querySelector('[data-cart-icon]');
    if (!cartIcon) return { x: window.innerWidth - 100, y: 30 };
    
    const rect = cartIcon.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  };

  return (
    <div ref={containerRef} className="fixed inset-0 pointer-events-none z-[100]">
      {flyingItems.map(item => {
        const endPosition = getCartIconPosition();
        const deltaX = endPosition.x - item.startPosition.x;
        const deltaY = endPosition.y - item.startPosition.y;
        
        return (
          <div
            key={item.id}
            className="absolute animate-fly-to-cart"
            style={{
              left: item.startPosition.x,
              top: item.startPosition.y,
              '--fly-x': `${deltaX}px`,
              '--fly-y': `${deltaY}px`,
            } as React.CSSProperties}
          >
            {/* Main spark/flash effect */}
            <div className="relative">
              {/* Central bright flash */}
              <div className="absolute w-10 h-10 -translate-x-1/2 -translate-y-1/2">
                <div className="absolute inset-0 bg-yellow-400 rounded-full blur-sm animate-pulse-fast shadow-lg shadow-yellow-500/50" />
                <div className="absolute inset-0 bg-orange-300 rounded-full scale-75 animate-pulse-fast" style={{ animationDelay: '0.1s' }} />
                <div className="absolute inset-0 bg-white rounded-full scale-50" />
              </div>
              
              {/* Trailing sparkles */}
              {[...Array(8)].map((_, i) => (
                <div
                  key={`sparkle-${i}`}
                  className="absolute rounded-full shadow-lg"
                  style={{
                    width: `${6 - i * 0.5}px`,
                    height: `${6 - i * 0.5}px`,
                    left: `${-15 - i * 8}px`,
                    top: `${-5 + Math.sin(i * 0.5) * 8}px`,
                    background: `radial-gradient(circle, #fbbf24 0%, #f59e0b 50%, #dc2626 100%)`,
                    boxShadow: `0 0 ${8 - i}px #fbbf24, 0 0 ${12 - i}px #f59e0b`,
                    opacity: 1 - (i * 0.1),
                    animation: `twinkle ${0.4 + i * 0.05}s ease-out infinite`,
                    animationDelay: `${i * 0.05}s`
                  }}
                />
              ))}
              
              {/* Star burst effect */}
              <svg className="absolute -translate-x-1/2 -translate-y-1/2 w-20 h-20 animate-spin-slow">
                <defs>
                  <radialGradient id={`starGradient-${item.id}`}>
                    <stop offset="0%" stopColor="#fde047" stopOpacity="1" />
                    <stop offset="50%" stopColor="#facc15" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
                  </radialGradient>
                </defs>
                {[...Array(8)].map((_, i) => (
                  <path
                    key={i}
                    d="M40,40 L40,25 L40,40"
                    stroke={`url(#starGradient-${item.id})`}
                    strokeWidth="3"
                    fill="none"
                    transform={`rotate(${i * 45} 40 40)`}
                    opacity={1}
                  />
                ))}
              </svg>
              
              {/* Glowing tail */}
              <div 
                className="absolute w-32 h-3 -translate-y-1/2"
                style={{
                  left: '-25px',
                  transformOrigin: 'right center',
                  transform: `rotate(${Math.atan2(deltaY, deltaX) * 180 / Math.PI + 180}deg)`,
                  background: `linear-gradient(to right, #fbbf24 0%, #f59e0b 30%, #dc2626 60%, transparent 100%)`,
                  boxShadow: '0 0 10px #fbbf24, 0 0 20px #f59e0b',
                  filter: 'blur(1px)'
                }}
              />
              
              {/* Extra glow effect */}
              <div className="absolute w-16 h-16 -translate-x-1/2 -translate-y-1/2">
                <div 
                  className="absolute inset-0 rounded-full animate-pulse"
                  style={{
                    background: 'radial-gradient(circle, rgba(251, 191, 36, 0.6) 0%, rgba(245, 158, 11, 0.3) 50%, transparent 70%)',
                    filter: 'blur(8px)'
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FlyingCartAnimation;